import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { postReq, extractErrorMessage } from "../../utils/request";
import { validatePassword } from "../../utils/passwordHelper";
import Button from "../../components/Button";
import Input from "../../components/Input";
import auth from "./auth.module.scss";

/**
 * Public page for redeeming a mobile invite code: exchange a one-time code for
 * a password, then sign in on the device.
 *
 * Convenience: accepts ?code=... in the URL so admins can paste a copied
 * code straight from the invite-sent toast.
 *
 * Public route, no auth — anyone with a valid code can redeem. The code
 * itself is the proof of identity.
 *
 * SECURITY: this page must never display the issued tokens. It used to render
 * the access and refresh tokens in click-to-copy inputs plus a raw /mobile/me
 * dump for manual testing — on an unauthenticated route, which put live
 * credentials on screen (and into screenshots and shoulder-surfing range).
 * Redemption success is now confirmed with a message only.
 */
const RedeemInvite = () => {
    const [searchParams] = useSearchParams();
    const [code, setCode] = useState<string>(searchParams.get("code") ?? "");
    const [password, setPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>("");
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (searchParams.get("code")) {
            const el = document.getElementById("redeem-password");
            if (el) (el as HTMLInputElement).focus();
        }
    }, [searchParams]);

    const handleRedeem = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        if (!code.trim()) {
            setError("Code is required");
            return;
        }
        if (!password || !confirmPassword) {
            setError("Both password fields are required");
            return;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }
        const pwError = validatePassword(password);
        if (pwError) {
            setError(pwError);
            return;
        }

        try {
            setSubmitting(true);
            const response = await postReq("users/redeem-invite", {
                code: code.trim(),
                newPassword: password,
                confirmPassword,
            });
            if (response.status === 200) {
                setSuccess(true);
            } else {
                setError(extractErrorMessage(response.body, "Redemption failed"));
            }
        } catch (err: any) {
            setError(err?.message ?? "Redemption failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleReset = () => {
        setSuccess(false);
        setCode("");
        setPassword("");
        setConfirmPassword("");
    };

    return (
        <div className="container">
            <div className="row justify-content-center">
                <div className="col-md-6">
                    <div className="d-flex justify-content-between align-items-center">
                        <span className="componentTitle">Redeem mobile invite</span>
                    </div>

                    {!success && (
                        <form className={auth.authForm} onSubmit={handleRedeem}>
                            <div className={auth.authField}>
                                <Input
                                    type="text"
                                    id="redeem-code"
                                    label="Invite code"
                                    value={code}
                                    onChange={(_, value) => setCode(String(value))}
                                    nativeProps={{
                                        autoComplete: "off",
                                        autoFocus: true,
                                        autoCapitalize: "none",
                                        autoCorrect: "off",
                                        spellCheck: false,
                                    }}
                                />
                            </div>
                            <div className={auth.authField}>
                                <Input
                                    type="password"
                                    id="redeem-password"
                                    label="New password"
                                    value={password}
                                    onChange={(_, value) => setPassword(String(value))}
                                    trimOnBlur={false}
                                    nativeProps={{ autoComplete: "new-password" }}
                                />
                            </div>
                            <div className={auth.authField}>
                                <Input
                                    type="password"
                                    id="redeem-confirm"
                                    label="Confirm new password"
                                    value={confirmPassword}
                                    onChange={(_, value) => setConfirmPassword(String(value))}
                                    trimOnBlur={false}
                                    nativeProps={{ autoComplete: "new-password" }}
                                />
                            </div>

                            {error && (
                                <span className="errorClass">{error}</span>
                            )}

                            <div className={auth.authActions}>
                                <Button type="submit" variant="primary" size="lg" loading={submitting} loadingText="Redeeming…" title="Set your password and activate the mobile app account">
                                    Redeem invite
                                </Button>
                            </div>
                        </form>
                    )}

                    {success && (
                        <div>
                            <div className="alert alert-success py-2 mb-3" role="alert">
                                Invite redeemed successfully. Sign in on the mobile app with your
                                new password.
                            </div>

                            <div className={auth.authActions}>
                                <Button
                                    type="button"
                                    variant="primary" size="lg"
                                    title="Clear the form to redeem another invite"
                                    onClick={handleReset}
                                >
                                    Redeem another
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RedeemInvite;
